import { Link } from "@tanstack/react-router";
import {
  DEFAULT_SHRINKAGE,
  type SortDirection,
  type SortSpec,
  applySort,
  artistScore,
  artistSorts,
  coverage,
  rank,
} from "@trackmark/core";
import { RankTable, rankTableStyles as table } from "../design/RankTable.js";
import { SortHead } from "../design/Sort.js";
import styles from "./Rankings.module.css";

export interface ArtistRow {
  id: number;
  name: string;
  ratedCount: number;
  raw: number | null;
  ownedCount: number;
  albumCount: number;
}

/** An artist with everything the table prints already worked out — including their place. */
export interface RankedArtist {
  artist: ArtistRow;
  rank: number | null;
  repeat: boolean;
  score: ReturnType<typeof artistScore>;
  coverage: number;
  /** `coverage()`'s own judgement, not a second threshold written here. */
  coverageLow: boolean;
}

export const ARTIST_SORTS: Record<string, SortSpec<RankedArtist>> = artistSorts<RankedArtist>({
  rank: (r) => r.rank,
  name: (r) => r.artist.name,
  // The DISPLAYED score, which is null below the threshold — the same "no number yet" the column
  // prints as `4 / 10 SONGS RATED`, so ordering by score agrees with what is on the screen.
  score: (r) => (r.score.qualified ? r.score.score : null),
  raw: (r) => r.score.raw,
  rated: (r) => r.score.ratedCount,
  albums: (r) => r.artist.albumCount,
  coverage: (r) => r.coverage,
});

/**
 * Score, place and coverage for every artist, in rank order.
 *
 * Exported because the jump rail lives outside this component and has to file rows the table is
 * actually in — one function, called by both, rather than two derivations that agree until the
 * threshold moves.
 */
export function rankedArtists(
  artists: ArtistRow[],
  collection: { totalRated: number; mean: number | null },
  settings = DEFAULT_SHRINKAGE,
): RankedArtist[] {
  const scored = artists.map((a) => ({
    artist: a,
    // Ratings are reconstructed from the mean and count: the endpoint sends ingredients, the
    // shared function does the arithmetic.
    score: artistScore(a.raw === null ? [] : Array.from({ length: a.ratedCount }, () => a.raw!), collection, settings),
  }));
  return rank(scored, (s) => s.score.score).map((r) => {
    const cov = coverage(r.item.score.ratedCount, r.item.artist.ownedCount);
    return {
      artist: r.item.artist,
      rank: r.rank,
      repeat: r.repeat,
      score: r.item.score,
      coverage: cov.ratio,
      coverageLow: cov.low,
    };
  });
}

/** Ranked, then put in whatever order the screen is asking for. */
export function orderedArtists(
  artists: ArtistRow[],
  collection: { totalRated: number; mean: number | null },
  settings: typeof DEFAULT_SHRINKAGE,
  sort: string,
  direction: SortDirection,
): RankedArtist[] {
  const ranked = rankedArtists(artists, collection, settings);
  const spec = ARTIST_SORTS[sort];
  return spec ? applySort(ranked, spec, direction) : ranked;
}

export interface ArtistTableProps {
  rows: RankedArtist[];
  collection: { totalRated: number; mean: number | null };
  settings?: typeof DEFAULT_SHRINKAGE;
  sort: string;
  direction: SortDirection;
  onSort: (sort: string, direction: SortDirection) => void;
  jumpTo?: { index: number; token: number } | null;
}

const ARTIST_COLUMNS = "66px 1fr 92px 78px 76px 92px 150px";

/**
 * The artist table, shared by artist rankings and the library's ARTISTS tab.
 *
 * Shared rather than reimplemented because §6 of design/README.md asks for "the same treatment as
 * artist rankings" — and the shrinkage caption is the part that must not drift, since it is the
 * only place the interface explains why an artist's score is not their mean.
 *
 * The caption sits BELOW the table now rather than inside the well above it. It is a footnote
 * about the SCORE column — a sentence you read once and then stop seeing — and at the top of the
 * screen it pushed the first row of a 780-row table down behind two lines of small print.
 */
export function ArtistTable({ rows, collection, settings = DEFAULT_SHRINKAGE, sort, direction, onSort, jumpTo = null }: ArtistTableProps) {
  const head = (field: string, label: string, align: "left" | "right" = "right", initial: SortDirection = "desc") => (
    <SortHead
      label={label}
      align={align}
      active={sort === field}
      direction={direction}
      onSort={() => onSort(field, sort === field ? (direction === "asc" ? "desc" : "asc") : initial)}
    />
  );

  return (
    <>
      <RankTable
        rows={rows}
        columns={ARTIST_COLUMNS}
        rowKey={(r) => r.artist.id}
        jumpTo={jumpTo}
        header={
          <>
            {/* Rank opens at number one; every other column opens at its top. */}
            {head("rank", "RANK", "left", "asc")}
            {head("name", "ARTIST", "left", "asc")}
            {head("score", "SCORE")}
            {head("raw", "RAW")}
            {head("rated", "RATED")}
            {head("albums", "ALBUMS")}
            {head("coverage", "COVERAGE")}
          </>
        }
        renderRow={(r) => {
          const { score, raw, qualified, penalised, ratedCount } = r.score;
          return (
            <>
              <div className={table.rank} data-repeat={r.repeat || undefined} data-unranked={r.rank === null || undefined}>
                {r.rank ?? "—"}
              </div>
              <Link
                to="/artist/$artistId"
                params={{ artistId: String(r.artist.id) }}
                className={table.titleLink}
                data-testid="artist-link"
              >
                {r.artist.name}
              </Link>
              {qualified ? (
                <div className={table.score}>{score!.toFixed(2)}</div>
              ) : (
                <div className={styles.belowThreshold} data-testid="below-threshold">
                  {ratedCount} / {settings.artistThreshold} SONGS RATED
                  <div
                    className={styles.thresholdRule}
                    style={{ width: `${(ratedCount / settings.artistThreshold) * 100}%` }}
                  />
                </div>
              )}
              <div className={styles.raw} data-penalised={penalised || undefined}>
                {raw === null ? "—" : raw.toFixed(2)}
              </div>
              <div className={styles.rated} data-testid="artist-rated">
                {ratedCount}
              </div>
              <div className={table.meta}>{r.artist.albumCount}</div>
              <div className={styles.coverage}>
                <div className={styles.coverageTrack}>
                  <div
                    className={styles.coverageFill}
                    data-low={r.coverageLow || undefined}
                    style={{ width: `${Math.round(r.coverage * 84)}px` }}
                  />
                </div>
                <span className={table.meta}>{Math.round(r.coverage * 100)}%</span>
              </div>
            </>
          );
        }}
      />

      <div className={styles.captionBelow} data-testid="shrinkage-caption">
        SCORE IS ADJUSTED FOR SAMPLE SIZE. AN ARTIST WITH 12 RATED SONGS IS A SMALLER CLAIM THAN ONE WITH 146.
        SHRINKAGE CONSTANT M = <b>{settings.m}</b>.{" "}
        {collection.totalRated < settings.priorMinRated ? (
          <>
            UNDER {settings.priorMinRated} RATED SONGS, SCORES SHRINK TOWARD A FIXED PRIOR OF{" "}
            <b>{settings.priorFixed.toFixed(1)}</b> RATHER THAN A COLLECTION MEAN THAT WOULD BE NOISE.
          </>
        ) : (
          <>SCORES SHRINK TOWARD THE COLLECTION MEAN OF <b>{collection.mean?.toFixed(2)}</b>.</>
        )}
      </div>
    </>
  );
}
